import React, { useState } from 'react';
import { useSendMessage, useThread } from '../../shared/api/data';
import type { XcpChat } from '../../shared/fixtures/types';
import '../../app/App.css';

interface ChatColumnProps {
  chat: XcpChat | null;
}

/** Chat column (C1). Thread → GET /cockpit-chat/history; composer → POST /customer-chat (Idempotency-Key). */
export function ChatColumn({ chat }: ChatColumnProps): React.ReactElement {
  const thread = useThread(chat?.id ?? null);
  const send = useSendMessage();
  const [draft, setDraft] = useState('');

  const submit = () => {
    const text = draft.trim();
    if (!chat || !text || send.isPending) return;
    setDraft('');
    send.mutate({ chatId: chat.id, text });
  };

  return (
    <div className="chat">
      <header className="chatHead">
        <span className="chatTitle">{chat ? chat.name : 'No chat selected'}</span>
        {chat && <span className="chatScope">· {chat.project}</span>}
      </header>

      <div className="thread" role="log" aria-live="polite" aria-label="Conversation">
        {!chat && <div className="stateNote">Pick a chat from the left to view the conversation.</div>}
        {chat && thread.isLoading && <div className="stateNote">Loading thread…</div>}
        {chat && thread.data?.data.length === 0 && (
          <div className="stateNote">No messages yet. Start the conversation below.</div>
        )}
        {thread.data?.data.map((m) => (
          <div key={m.seq} className={`msg ${m.role === 'you' ? 'msgYou' : 'msgAsst'}`}>
            <span className="msgRole">{m.role === 'you' ? 'You' : 'Assistant'}</span>
            <div className="msgBubble">{m.text}</div>
            {m.echo && (
              <span className="msgEcho">
                <span className="statePill" data-state={m.echo.state}>
                  {m.echo.kind}
                </span>
                {m.echo.title}
              </span>
            )}
          </div>
        ))}
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label htmlFor="composer-input" style={{ position: 'absolute', left: -9999 }}>
          Message
        </label>
        <textarea
          id="composer-input"
          className="composerInput"
          placeholder={chat ? `Message ${chat.name}…` : 'Select a chat to begin'}
          value={draft}
          disabled={!chat || send.isPending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={1}
        />
        <button type="submit" className="sendBtn" disabled={!chat || !draft.trim() || send.isPending}>
          {send.isPending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
